/**
 * The console's one piece of shared state: the public configuration and the
 * session. Everything else a view needs it fetches for itself.
 *
 * A context rather than a prop chain because the print views of §11.6 are
 * routed separately from the shell — a kitchen document opens in its own tab
 * and has no console chrome around it — and both have to reach the same
 * session without the router carrying it.
 */

import { createContext, useContext } from 'react'

import { readPublicConfig, type PublicConfig } from '../lib/env.ts'
import type { StoredSession } from './session.ts'

export interface ConsoleContextValue {
  config: PublicConfig
  session: StoredSession | null
  setSession: (session: StoredSession | null) => void
}

export const ConsoleContext = createContext<ConsoleContextValue | null>(null)

export function useConsole(): ConsoleContextValue {
  const value = useContext(ConsoleContext)
  if (value === null) {
    // A view rendered outside the provider is a wiring mistake, not a state
    // the console can be in; saying so by name beats a null dereference.
    return { config: readPublicConfig(), session: null, setSession: () => {} }
  }
  return value
}
